import React from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useSmartTranslation } from '../../../../../../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../../../../../../generic/hooks/useDateFormatter'
import ReferenceThumbnail from './ReferenceThumbnail'
import { EMPTY_VALUE, idealStep } from '../../utils/jobDetailFormat'
import { Pill } from './JobDetailPrimitives'
import { systemValueLabel } from '../../../../../../generic/systemValues/systemValueLabel'

// Historia 160 (CA1.7, mock 1) — cabecera: foto de la referencia, "código nombre · tecnología ·
// paso i de N", línea de la OF y, arriba a la derecha (D8), acciones de estado, QR y cerrar.
//
// G8/RN51: un dato nulo se pinta "—" y SE OMITE el separador " · " que lo uniría a su vecino; nunca
// "null", "undefined" ni un separador doble o colgando (S22).

const SEPARATOR = ' · '

/** Une segmentos: con valor, " · valor"; sin él, " —" (sin su separador). */
const Segments = ({ segments = [], testId = '' }) => (
  <Box component='span' data-testid={testId || undefined}>
    {segments.map((segment, index) => {
      const present = segment.value !== '' && segment.value !== null && segment.value !== undefined
      const lead = index === 0 ? '' : (present ? SEPARATOR : ' ')
      return (
        <Box component='span' key={segment.key} data-testid={segment.testId || undefined}>
          {lead}{present ? segment.value : EMPTY_VALUE}
        </Box>
      )
    })}
  </Box>
)

Segments.propTypes = { segments: PropTypes.array, testId: PropTypes.string }

const JobDetailHeader = ({ detail = {}, actions = null, qr = null, onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const { formatDate } = useDateFormatter()
  const job = detail.job || {}
  const fabricationOrder = detail.fabricationOrder || {}
  const partReference = detail.partReference || {}
  const step = idealStep(detail.sequence || [], job.jobReferenceId)
  const isRework = job.rework === true || step.current === 0

  const jobSegments = [
    { key: 'technology', testId: 'job-technology', value: systemValueLabel(t_, 'technology', job.technology) }
  ]
  const orderSegments = [
    { key: 'code', value: fabricationOrder.code || '' },
    { key: 'description', testId: 'order-description', value: fabricationOrder.description || '' },
    { key: 'reference', value: [partReference.code, partReference.name].filter(Boolean).join(' ') },
    // The OF's TARGET date (what planning committed to), not its delivery date: the delivery date is the
    // customer's and stays available in the QR popover. Null paints the "—" placeholder like any segment.
    {
      key: 'target',
      testId: 'order-target-date',
      value: fabricationOrder.targetDate ? t_('Target {{date}}', { date: formatDate(fabricationOrder.targetDate) }) : ''
    }
  ]

  return (
    <Stack direction='row' alignItems='center' spacing={2.5} sx={{ px: 3, py: 2 }} data-testid='job-detail-header'>
      <ReferenceThumbnail partReference={partReference} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction='row' alignItems='baseline' spacing={1.5} flexWrap='wrap' useFlexGap>
          <Typography component='h2' sx={{ fontSize: '2rem', fontWeight: 800, lineHeight: 1.1 }} data-testid='job-code'>
            {job.code || ''}
          </Typography>
          <Typography sx={{ fontSize: '1.45rem', fontWeight: 700 }} data-testid='job-name'>{job.name || ''}</Typography>
          <Typography component='span' color='text.secondary' sx={{ fontSize: '1.1rem' }}>
            <Segments segments={jobSegments} testId='job-subtitle' />
            {isRework
              ? <>{SEPARATOR}<Pill label={t_('Rework')} tone='warning' testId='job-rework-mark' /></>
              : <span data-testid='job-step'>{SEPARATOR}{t_('Step {{current}} of {{total}}', { current: step.current, total: step.total })}</span>}
          </Typography>
        </Stack>
        <Typography color='text.secondary' sx={{ fontSize: '1.05rem', mt: 0.5 }} data-testid='order-line'>
          <Segments segments={orderSegments} />
        </Typography>
      </Box>
      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ flexShrink: 0 }}>
        {actions}
        {qr}
        <IconButton aria-label={t_('Close')} onClick={onClose} sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, width: 48, height: 48 }}>
          <CloseIcon />
        </IconButton>
      </Stack>
    </Stack>
  )
}

JobDetailHeader.propTypes = {
  detail: PropTypes.object,
  actions: PropTypes.node,
  qr: PropTypes.node,
  onClose: PropTypes.func
}

export default JobDetailHeader
